import React, { useState } from 'react';
import { Clapperboard, Monitor, Smartphone } from 'lucide-react';

const DURATIONS = [
    { value: '15 seconds', label: '15s', desc: 'Quick clip' },
    { value: '30 seconds', label: '30s', desc: 'Short form' },
    { value: '45 seconds', label: '45s', desc: 'Standard' },
    { value: '60 seconds', label: '60s', desc: 'Extended' },
    { value: '75 seconds', label: '75s', desc: 'Long form' },
    { value: '90 seconds', label: '90s', desc: 'Long form+' },
    { value: '120 seconds', label: '120s', desc: 'Feature' },
];

const RATIOS = [
    { value: '16:9', label: '16:9', desc: 'Landscape', icon: Monitor, w: 48, h: 27 },
    { value: '9:16', label: '9:16', desc: 'Portrait', icon: Smartphone, w: 27, h: 48 },
];

export const ProjectSetup = ({ initialData, onSubmit }) => {
    const [name, setName] = useState(initialData.name);
    const [duration, setDuration] = useState(initialData.duration || '60 seconds');
    const [aspectRatio, setAspectRatio] = useState(initialData.aspectRatio || '16:9');

    const handleSubmit = (e) => {
        e.preventDefault();
        if (name.trim()) {
            onSubmit({ name, duration, aspectRatio });
        }
    };

    return (
        <div className="w-full h-full overflow-y-auto flex items-center justify-center p-6" style={{ background: '#050505', fontFamily: "'Manrope', sans-serif" }}>
            <div className="w-full max-w-xl">
                {/* Card */}
                <div className="rounded-2xl border border-white/10 overflow-hidden shadow-[0_8px_40px_rgba(0,0,0,0.5)] backdrop-blur-xl" style={{ background: 'rgba(15, 17, 21, 0.85)' }}>
                    {/* Header */}
                    <div className="px-8 pt-8 pb-2">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="w-10 h-10 rounded-xl bg-violet-500/10 border border-violet-500/20 flex items-center justify-center">
                                <Clapperboard className="w-5 h-5 text-violet-400" />
                            </div>
                            <div>
                                <h1 className="text-xl font-bold text-white tracking-tight">New Project</h1>
                                <p className="text-xs text-zinc-500">Set up the basics for your film</p>
                            </div>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="px-8 pb-8">
                        {/* Project Name */}
                        <div className="mb-6 mt-4">
                            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400 mb-2 block">Project Name</label>
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="e.g., Cosmic Echoes"
                                className="w-full px-4 py-3.5 bg-white/5 border border-white/10 rounded-xl text-white text-sm placeholder-zinc-600 focus:outline-none focus:border-violet-500/50 focus:ring-1 focus:ring-violet-500/20 transition-all"
                                data-testid="project-name-input"
                                autoFocus
                            />
                        </div>

                        {/* Duration */}
                        <div className="mb-6">
                            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400 mb-3 block">Duration</label>
                            <div className="grid grid-cols-4 sm:grid-cols-7 gap-2" data-testid="duration-selector">
                                {DURATIONS.map(d => {
                                    const isActive = duration === d.value;
                                    return (
                                        <button
                                            key={d.value}
                                            type="button"
                                            onClick={() => setDuration(d.value)}
                                            className={`py-2.5 rounded-xl border text-center transition-all duration-200 ${
                                                isActive
                                                    ? 'bg-violet-500/15 border-violet-500/40 ring-1 ring-violet-500/20'
                                                    : 'bg-white/5 border-white/10 hover:border-white/20 hover:bg-white/8'
                                            }`}
                                            data-testid={`duration-${d.value}`}
                                        >
                                            <span className={`text-sm font-bold block ${isActive ? 'text-violet-400' : 'text-zinc-300'}`}>{d.label}</span>
                                            <span className={`text-[10px] ${isActive ? 'text-violet-500/60' : 'text-zinc-600'}`}>{d.desc}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Aspect Ratio */}
                        <div className="mb-8">
                            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400 mb-3 block">Aspect Ratio</label>
                            <div className="grid grid-cols-2 gap-3" data-testid="aspect-ratio-selector">
                                {RATIOS.map(r => {
                                    const isActive = aspectRatio === r.value;
                                    return (
                                        <button
                                            key={r.value}
                                            type="button"
                                            onClick={() => setAspectRatio(r.value)}
                                            className={`relative flex flex-col items-center gap-2 py-4 rounded-xl border transition-all duration-200 ${
                                                isActive
                                                    ? 'bg-violet-500/15 border-violet-500/40 ring-1 ring-violet-500/20'
                                                    : 'bg-white/5 border-white/10 hover:border-white/20 hover:bg-white/8'
                                            }`}
                                            data-testid={`ratio-${r.value}`}
                                        >
                                            {/* Visual frame preview */}
                                            <div
                                                className={`border-2 rounded-sm ${isActive ? 'border-violet-400' : 'border-zinc-600'}`}
                                                style={{ width: r.w * 0.65, height: r.h * 0.65 }}
                                            />
                                            <div className="text-center">
                                                <span className={`text-xs font-bold block ${isActive ? 'text-violet-400' : 'text-zinc-300'}`}>{r.label}</span>
                                                <span className={`text-[10px] ${isActive ? 'text-violet-500/60' : 'text-zinc-600'}`}>{r.desc}</span>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Submit */}
                        <button
                            type="submit"
                            disabled={!name.trim()}
                            className="w-full flex items-center justify-center gap-2 bg-violet-600 hover:bg-violet-700 disabled:bg-zinc-800 disabled:text-zinc-600 disabled:cursor-not-allowed text-white font-bold py-3.5 rounded-full transition-all hover:-translate-y-0.5 text-sm"
                            data-testid="create-project-submit"
                        >
                            <Clapperboard className="w-4 h-4" />
                            Create Project
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};
